import { useContext, useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import {
  terminalOutline, eyeOutline, notificationsOutline, rocketOutline,
  flaskOutline, bugOutline, hammerOutline, analyticsOutline, searchOutline,
  codeSlashOutline, cloudDownloadOutline, cloudUploadOutline, chatbubbleEllipsesOutline,
  documentTextOutline, bandageOutline, gitPullRequestOutline, gitNetworkOutline,
  mailOutline, shieldCheckmarkOutline, sparklesOutline, checkmarkDoneOutline,
  serverOutline, listOutline, sendOutline, refreshOutline, trashOutline,
} from 'ionicons/icons';
import { WorkflowActionsContext } from '../contexts/WorkflowActionsContext';
import { CartoonPreflight, CartoonExecuteLive, CartoonGenerateScript } from './CartoonWorkflowIcons';
import { RunStack, runningClass } from './RunStack';
import './WorkflowNode.css';

// Semantic icon picker: maps a node's type/label keywords to a fitting icon so
// every template gets meaningful glyphs instead of a generic terminal. First
// match wins; order from specific → generic. Falls back to terminalOutline.
const ICON_RULES = [
  [/review|inspect|audit/, eyeOutline],
  [/pull[\s_-]?request|\bpr\b|merge/, gitPullRequestOutline],
  [/notify|notification|alert/, notificationsOutline],
  [/deploy|release|publish|ship/, rocketOutline],
  [/upload|push|sync[\s_-]?up/, cloudUploadOutline],
  [/clone|fetch|download|pull|checkout/, cloudDownloadOutline],
  [/clone[\s_-]?repo|git|repo|branch/, gitNetworkOutline],
  [/preflight|test|verify|validate|qa/, flaskOutline],
  [/bug|triage|error|sentry|crash/, bugOutline],
  [/build|compile|bundle|package/, hammerOutline],
  [/analy|metric|report|score|stat/, analyticsOutline],
  [/search|scan|find|discover|lookup/, searchOutline],
  [/generate[\s_-]?script|script|codegen|code/, codeSlashOutline],
  [/comment|reply|respond|message|chat/, chatbubbleEllipsesOutline],
  [/summar|document|doc|write[\s_-]?up|draft/, documentTextOutline],
  [/fix|patch|repair|autofix|remediat/, bandageOutline],
  [/email|mail/, mailOutline],
  [/security|auth|permission|guard|protect/, shieldCheckmarkOutline],
  [/enrich|enhance|augment|polish/, sparklesOutline],
  [/finalize|complete|done|approve|confirm/, checkmarkDoneOutline],
  [/execute|run|server|host|provision/, serverOutline],
  [/list|collect|gather|enumerate/, listOutline],
  [/send|dispatch|post|emit/, sendOutline],
  [/refresh|retry|reload|update/, refreshOutline],
  [/delete|remove|cleanup|purge/, trashOutline],
];

function pickNodeIcon(nodeType, label) {
  const hay = `${nodeType || ''} ${label || ''}`.toLowerCase();
  for (const [re, icon] of ICON_RULES) {
    if (re.test(hay)) return icon;
  }
  return terminalOutline;
}

const EditableLabel = ({ label, editing, setEditing, editValue, setEditValue, commitRename, inputRef, className }) => (
  <div className={`gwe-workflow-node__label-wrap ${className || ''}`}>
    <div
      className="gwe-workflow-node__label gwe-workflow-node__label--editable"
      style={{ visibility: editing ? 'hidden' : 'visible' }}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {label}
    </div>
    {editing && (
      <input
        ref={inputRef}
        className="gwe-workflow-node__label-input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setEditValue(label); setEditing(false); } }}
        onClick={(e) => e.stopPropagation()}
      />
    )}
  </div>
);

export const WorkflowNode = ({ data, isConnectable }) => {
  const {
    label, status = 'pending',
    showAddButton = false, nodeId, toolCount = 0,
    customCode = null, nodeType,
    description = null, descriptionFull = null
  } = data;
  const hasCustomCode = !!customCode;
  const actions = useContext(WorkflowActionsContext);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef(null);

  useEffect(() => { setEditValue(label); }, [label]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== label) {
      actions?.onRenameNode(nodeId, trimmed);
    } else {
      setEditValue(label);
    }
    setEditing(false);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    actions?.onNodeContextMenu(nodeId, { x: e.clientX, y: e.clientY });
  };

  const statusColors = {
    pending: '#8c8c8c',
    running: '#4a90e2',
    completed: '#52c41a',
    success: '#52c41a',
    failed: '#ff4d4f',
    error: '#ff4d4f',
    cancelled: '#ff9800'
  };

  const statusColor = statusColors[status] || statusColors.pending;
  const nodeIcon = pickNodeIcon(nodeType, label);

  const useCartoonPreflight = nodeType === 'preflight';
  const useCartoonLive = nodeType === 'execute_live';
  const useCartoonScript = nodeType === 'generate_script';

  // RUNS ON THIS STEP right now (`data.activeRuns`, stamped by the surface that
  // knows where this agent's runs are — PipelineGraphView). Same treatment a
  // running MEMBER satellite gets: glowing white name, white tile edge, one
  // sheet of paper per run. Distinct from the legacy `status === 'running'`
  // above, which is a per-node EXECUTION status from the editor's demo surface
  // and is never set on the read-only agent graph.
  const activeRuns = Math.max(0, Number(data?.activeRuns) || 0);

  const rootClass = [
    'gwe-workflow-node',
    'gwe-workflow-node--action',
    status === 'running' && 'gwe-workflow-node--running',
    runningClass(activeRuns),
    // A dispatch node whose CHILD agent still has unset/keyless models: the
    // parent's run dies there, so the parent says so BEFORE you trigger it.
    data?.notReady && 'gwe-workflow-node--notready',
  ].filter(Boolean).join(' ');

  const handles = (
    <>
      <Handle type="target" position={Position.Top} id="top" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="target" position={Position.Left} id="target-left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="target" position={Position.Right} id="target-right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      {/* Bottom TARGET handle — the landing point for ANY loop-back edge (a
          purely geometric class: target left of source). The bottom lane rises
          straight into the node's underside instead of climbing its flank. */}
      <Handle type="target" position={Position.Bottom} id="target-bottom" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Top} id="source-top" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Bottom} id="bottom" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Left} id="left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="source" position={Position.Right} id="right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
    </>
  );

  return (
    <div
      className={rootClass}
      style={{ borderColor: statusColor }}
      data-node-status={status}
      onContextMenu={handleContextMenu}
    >
      {handles}

      <div className="gwe-workflow-node__action-layout">
        <div className="gwe-workflow-node__icon-box gwe-run-stack-host">
          <RunStack activeRuns={activeRuns} />
          {useCartoonPreflight ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonPreflight status={status} />
            </div>
          ) : useCartoonLive ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonExecuteLive status={status} />
            </div>
          ) : useCartoonScript ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonGenerateScript status={status} />
            </div>
          ) : (
            <IonIcon icon={nodeIcon} className="gwe-workflow-node__icon" />
          )}
        </div>
        {/* THE NAME, and only the name. A node that dispatches child agents is
            still an authored STEP with a name of its own; the agents it starts
            are drawn as member satellites hanging off it (utils/memberSatellites,
            connector `starts`), each with that agent's own icon and name. So
            there is no "⧉ N agents" count here (founder, 2026-08-25: "get rid of
            that count, it's useless" — it numbered things the reader can see)
            and no synthesized member BOX borrowing this component to print a
            name (replaced by the satellite, same day: "change this to this"). */}
        <EditableLabel
          label={label} editing={editing} setEditing={setEditing}
          editValue={editValue} setEditValue={setEditValue}
          commitRename={commitRename} inputRef={inputRef}
          className="gwe-workflow-node__label--below"
        />
        {description && (
          <div className="gwe-node-caption">
            <div className="gwe-workflow-node__description">
              {description}
            </div>
            <div className="gwe-node-caption__tip" role="tooltip">
              {descriptionFull || description}
            </div>
          </div>
        )}
        {(toolCount > 0 || hasCustomCode) && (
          <div className="gwe-workflow-node__badges">
            {toolCount > 0 && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--tools">
                {toolCount} tool{toolCount > 1 ? 's' : ''}
              </div>
            )}
            {hasCustomCode && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--custom">
                custom
              </div>
            )}
          </div>
        )}
      </div>

      {showAddButton && (
        <button
          className="gwe-workflow-node__add-btn"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            actions?.onRequestAddNode(nodeId, rect);
          }}
          title="Insert node after this step"
        >
          +
        </button>
      )}
    </div>
  );
};
